/**
 * ProactiveInsightsCard — ranked "what should I know right now?" tile.
 *
 * Mounted on the Mission Control Home (LearningIntelligenceEngine) and
 * the LEI Dashboard, ABOVE the PinnedReportsSection. Lazy-loads the
 * caller's proactive insights on mount (GET /mc/reports/proactive/insights),
 * which the backend serves from a 24h cache or recomputes on miss.
 *
 * UX rules:
 *   • Renders nothing when the user has no pinned reports OR Claude
 *     genuinely found nothing material — pinned reports + the empty
 *     state would just add noise above the (also-empty) pin grid.
 *   • Each insight is a single-line headline with a "kind" icon
 *     (warning / celebration / insight), an optional target chip,
 *     and a one-click "Investigate" button that opens the source
 *     report in the AI builder with the suggested prompt pre-filled.
 *   • Manual "Refresh" button forces recomputation (busts the cache).
 *
 * Failures are best-effort: a 500 / network error simply hides the
 * card rather than blocking the rest of the dashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ExternalLink,
  Lightbulb,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const KIND_STYLES = {
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-600',
    border: 'border-amber-200',
    bg: 'bg-amber-50/60',
    label: 'Warning',
    labelClass: 'text-amber-700 bg-amber-100',
  },
  celebration: {
    Icon: TrendingUp,
    iconClass: 'text-emerald-600',
    border: 'border-emerald-200',
    bg: 'bg-emerald-50/60',
    label: 'Win',
    labelClass: 'text-emerald-700 bg-emerald-100',
  },
  insight: {
    Icon: Sparkles,
    iconClass: 'text-indigo-600',
    border: 'border-indigo-200',
    bg: 'bg-indigo-50/60',
    label: 'Insight',
    labelClass: 'text-indigo-700 bg-indigo-100',
  },
};

function formatGeneratedAt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

export default function ProactiveInsightsCard({ surface = 'home' }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/mc/reports/proactive/insights');
      setDoc(data || null);
    } catch (e) {
      // Best-effort: hide the card on failure instead of blocking
      // the rest of the dashboard.
      // eslint-disable-next-line no-console
      console.warn('[proactive-insights] fetch failed', e?.response?.status);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post('/mc/reports/proactive/refresh');
      setDoc(data || null);
      toast.success('Insights refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refresh insights');
    } finally {
      setRefreshing(false);
    }
  };

  const onInvestigate = (insight) => {
    const rid = insight?.source_report_id;
    if (!rid) {
      toast.info('No source report attached to this insight');
      return;
    }
    const prompt = insight?.prompt || insight?.text || '';
    const qs = prompt ? `?prompt=${encodeURIComponent(prompt)}` : '';
    navigate(`/admin/reports/builder/${rid}${qs}`);
  };

  // Hide entire card when: still loading initial fetch + no doc, OR
  // the caller has no pinned reports at all (pin_count === 0), OR
  // Claude returned an empty insights list (nothing material).
  if (loading && !doc) return null;
  const insights = doc?.insights || [];
  const pinCount = doc?.pin_count ?? 0;
  if (!insights.length && pinCount === 0) return null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
      data-testid={`proactive-insights-${surface}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 p-2 flex-shrink-0">
            <Lightbulb className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900">
              What should I know right now?
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              AI-ranked material changes across your pinned reports
              {doc?.generated_at ? ` · updated ${formatGeneratedAt(doc.generated_at)}` : ''}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          data-testid="proactive-insights-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {insights.length === 0 ? (
        <div
          className="text-sm text-slate-500 italic px-1 py-2"
          data-testid="proactive-insights-empty"
        >
          Nothing material to flag right now — your pinned reports look steady.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="proactive-insights-list">
          {insights.map((it, i) => {
            const style = KIND_STYLES[it.kind] || KIND_STYLES.insight;
            const { Icon } = style;
            return (
              <li
                key={`${it.source_report_id || 'x'}-${i}`}
                className={`rounded-xl border ${style.border} ${style.bg} p-3 flex items-start gap-3`}
                data-testid={`proactive-insight-${i}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <Icon className={`h-4 w-4 ${style.iconClass}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${style.labelClass}`}
                    >
                      {style.label}
                    </span>
                    {it.target ? (
                      <span className="text-[11px] text-slate-600 font-medium">
                        {it.target}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-slate-800 leading-snug">{it.text}</p>
                </div>
                {it.source_report_id ? (
                  <button
                    type="button"
                    onClick={() => onInvestigate(it)}
                    className="flex-shrink-0 flex items-center gap-1 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
                    data-testid={`proactive-insight-investigate-${i}`}
                  >
                    Investigate
                    <ExternalLink className="h-3 w-3" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
